import type { InventoryBalance, InventoryMovement, Product, Warehouse } from "../types/api.js";
import { apiRequest } from "./api-client.js";

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, v);
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

export async function fetchProducts(
  token: string,
  params: { tenantId?: string; sku?: string } = {},
): Promise<{ products: Product[] }> {
  return apiRequest<{ products: Product[] }>(withQuery("/api/v1/products", params), { method: "GET", token });
}

export async function fetchWarehouses(
  token: string,
  params: { tenantId?: string } = {},
): Promise<{ warehouses: Warehouse[] }> {
  return apiRequest<{ warehouses: Warehouse[] }>(withQuery("/api/v1/warehouses", params), {
    method: "GET",
    token,
  });
}

export async function fetchInventoryBalances(
  token: string,
  params: { tenantId?: string; productId?: string; warehouseId?: string; locationId?: string; sku?: string } = {},
): Promise<{ balances: InventoryBalance[] }> {
  return apiRequest<{ balances: InventoryBalance[] }>(withQuery("/api/v1/inventory/balances", params), {
    method: "GET",
    token,
  });
}

export async function fetchInventoryMovements(
  token: string,
  params: {
    tenantId?: string;
    productId?: string;
    warehouseId?: string;
    locationId?: string;
    type?: string;
  } = {},
): Promise<{ movements: InventoryMovement[] }> {
  return apiRequest<{ movements: InventoryMovement[] }>(withQuery("/api/v1/inventory/movements", params), {
    method: "GET",
    token,
  });
}

/* —— Products CRUD (Fase 9 / web v2.1.0) —— */

export interface ProductCreateInput {
  sku: string;
  name: string;
  description?: string | null;
  unit?: string | null;
  isActive?: boolean;
  /** Solo plataforma puede informarlo; tenant-admin lo ignora (API valida). */
  tenantId?: string;
}

export interface ProductUpdateInput {
  sku?: string;
  name?: string;
  description?: string | null;
  unit?: string | null;
  isActive?: boolean;
}

export async function fetchProductById(token: string, productId: string): Promise<{ product: Product }> {
  return apiRequest<{ product: Product }>(`/api/v1/products/${productId}`, { method: "GET", token });
}

export async function createProductRequest(token: string, body: ProductCreateInput): Promise<{ product: Product }> {
  return apiRequest<{ product: Product }>("/api/v1/products", { method: "POST", token, body });
}

export async function updateProductRequest(
  token: string,
  productId: string,
  body: ProductUpdateInput,
): Promise<{ product: Product }> {
  return apiRequest<{ product: Product }>(`/api/v1/products/${productId}`, { method: "PATCH", token, body });
}

export async function deleteProductRequest(token: string, productId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/products/${productId}`, { method: "DELETE", token });
}

/* —— Warehouses CRUD (Fase 9 / web v2.1.0) —— */

export interface WarehouseCreateInput {
  code: string;
  name: string;
  isActive?: boolean;
  tenantId?: string;
}

export interface WarehouseUpdateInput {
  code?: string;
  name?: string;
  isActive?: boolean;
}

export async function fetchWarehouseById(token: string, warehouseId: string): Promise<{ warehouse: Warehouse }> {
  return apiRequest<{ warehouse: Warehouse }>(`/api/v1/warehouses/${warehouseId}`, { method: "GET", token });
}

export async function createWarehouseRequest(
  token: string,
  body: WarehouseCreateInput,
): Promise<{ warehouse: Warehouse }> {
  return apiRequest<{ warehouse: Warehouse }>("/api/v1/warehouses", { method: "POST", token, body });
}

export async function updateWarehouseRequest(
  token: string,
  warehouseId: string,
  body: WarehouseUpdateInput,
): Promise<{ warehouse: Warehouse }> {
  return apiRequest<{ warehouse: Warehouse }>(`/api/v1/warehouses/${warehouseId}`, { method: "PATCH", token, body });
}

export async function deleteWarehouseRequest(token: string, warehouseId: string): Promise<void> {
  await apiRequest<void>(`/api/v1/warehouses/${warehouseId}`, { method: "DELETE", token });
}
